import { useState, useEffect, useCallback, useMemo, createElement } from 'react';
import { render } from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import Head from 'next/head';
import Cookie from 'cookie';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import API from '@utils/api';
import { apiClient } from '@/http-client';
import { NEXT_PUBLIC_API_BASE_URL } from '@/app-configuration';
// import { formatPostData } from '@services/postService';
import { PageContext } from '@contexts';
import { reqLocationsAction } from '@stores/locations/actions';
import { useDealerSelector } from '@stores/dealer/selectors';
import { Layout, Container, Modal, Button, StepProgress } from '@components';
import {
  asDollars,
  asCurrency,
  getLngLat,
  getPostalCode,
  getProvince,
  getUniqueIdFromELOQUA
} from '@/helpers/buildandquotehelpers';
import { getLanguageId } from '@helpers/language';

import PostalCodeModal from '../PostalCodeModal';
import ContinueButton from './ContinueButton';
import QuoteButton from './QuoteButton';
import BuildImage from './BuildImage';
import EmailForm from './EmailForm';
import DealerHeader from '../DealerHeader';
import styles from './BuildCustomization.module.scss';
// Knockdown templates
import EnPostTextBody from './templates/en/postTextBody.html';
import EnSectionBody0 from './templates/en/sectionBody-0.html';
import EnSectionBody1 from './templates/en/sectionBody-1.html';
import EnSectionBody2 from './templates/en/sectionBody-2.html';
import FrPostTextBody from './templates/fr/postTextBody.html';
import FrSectionBody0 from './templates/fr/sectionBody-0.html';
import FrSectionBody1 from './templates/fr/sectionBody-1.html';
import FrSectionBody2 from './templates/fr/sectionBody-2.html';

/*
 * This function is used to insert the react componenents after
 * the page been populated with the knockdown templates (above).
 */
export const insertReactComponent = (domEl, Component, props, { isMultiple, interval } = { isMultiple: false, interval: null }) => {
  interval = setInterval(() => {
    if (isMultiple) {
      const domContainers = document.querySelectorAll(domEl);
      domContainers.forEach((domContainer) => {
        if (domContainer) {
          render(<Component {...props} />, domContainer);
          clearInterval(interval);
        }
      });
    } else {
      const domContainer = document.querySelector(domEl);
      if (domContainer) {
        render(<Component {...props} />, domContainer);
        clearInterval(interval);
      }
    }
  }, 100);
};

export const LegalDescription = ({ description }) => (
  <span>
    {description}
  </span>
);

const getProductModalImageSrc = (prodId) => {
  if (prodId.startsWith('*')) {
    prodId = prodId.substring(1);
  }
  return `/api/static/media/byfile/Products/${prodId}/Studio/1_N_N_N_I_640x640.png`;
};

export default function BuildCustomization(pageProps) {
  const dispatch = useDispatch();
  const router = useRouter();
  const { id } = router.query;
  const { locale } = router;

  const [manifestImageCode, setManifestImageCode] = useState('');
  const [showProductModal, setShowProductModal] = useState(false);
  const [showDependsModal, setShowDependsModal] = useState(false);
  const [modalInfo, setModalInfo] = useState({});
  const [formMode, setFormMode] = useState('configuration');
  const [showPostalModal, setShowPostalModal] = useState(false);

  const [postalCode, setPostalCode] = useState('');
  const [lngLat, setLngLat] = useState('');
  const [province, setProvince] = useState('');

  // TODO: finish dealer code
  const dealer = useSelector(useDealerSelector);

  const data = pageProps?.data;
  const baseModelLegal = data?.DisplayOptions?.find((item) => item?.Name === 'Legal')?.DisplayOptions?.find(
    (x) => x?.DisplayOptionKey === 'BaseModelLegal'
  );
  const summaryLegal = data?.DisplayOptions?.find((item) => item?.Name === 'Legal')?.DisplayOptions?.find(
    (x) => x?.DisplayOptionKey === 'SummaryLegal'
  );

  const addOnsEnabled = useMemo(() => data?.ProductDetail?.CompatibleImplements?.length > 0
    && data?.ProductDetail?.DisplayOptions?.find((item) => item?.Name === 'Compatible Implements')?.DisplayOptions?.find(
      (x) => x?.DisplayOptionKey === 'DisplayInBQ'
    )?.Enabled, [data]);

  // Handlers
  const showCustomize = () => {
    const emailContainer = document.querySelector('#emailFormContainer');
    const dispHolder = document.querySelector('#sbDispHolder');
    const stepbar = document.querySelector('#stepbar');
    if (emailContainer && dispHolder) {
      emailContainer.style.display = 'none';
      dispHolder.style.display = 'block';
      dispHolder?.classList.remove('addOns', 'summary');
      dispHolder.classList.add('customize');
      window.scrollTo(0, 0);
      if (stepbar) {
        stepbar.childNodes[2].classList.add(stepbar.firstChild.classList[1]);
      }
    }
  };

  const showAddOns = () => {
    const emailContainer = document.querySelector('#emailFormContainer');
    const dispHolder = document.querySelector('#sbDispHolder');
    const stepbar = document.querySelector('#stepbar');
    if (emailContainer && dispHolder) {
      emailContainer.style.display = 'none';
      dispHolder.style.display = 'block';
      dispHolder?.classList.remove('customize', 'summary');
      dispHolder.classList.add('addOns');
      window.scrollTo(0, 0);
      if (stepbar) {
        stepbar.childNodes[2].classList.add(stepbar.firstChild.classList[1]);
        stepbar.childNodes[2].classList.add(stepbar.firstChild.classList[1]);
      }
    }
  };

  const showSummary = () => {
    const emailContainer = document.querySelector('#emailFormContainer');
    const dispHolder = document.querySelector('#sbDispHolder');
    const stepbar = document.querySelector('#stepbar');
    if (emailContainer && dispHolder) {
      emailContainer.style.display = 'none';
      dispHolder.style.display = 'block';
      dispHolder?.classList.remove('customize', 'addOns');
      dispHolder.classList.add('summary');
      window.scrollTo(0, 0);
      if (stepbar) {
        stepbar.childNodes[2].classList.add(stepbar.firstChild.classList[1]);
        stepbar.childNodes[3].classList.add(stepbar.firstChild.classList[1]);
        stepbar.lastChild.classList.add(stepbar.firstChild.classList[1]);
      }
    }
  };

  const showQuote = () => {
    const emailContainer = document.querySelector('#emailFormContainer');
    const dispHolder = document.querySelector('#sbDispHolder');
    if (emailContainer && dispHolder) {
      emailContainer.style.display = 'block';
      dispHolder.style.display = 'none';
      window.scrollTo(0, 0);
    }
  };

  const continueButtonClicked = () => {
    setFormMode('summary');
    showSummary();
  };

  const quoteButtonClicked = () => {
    if (formMode === 'configuration') {
      setFormMode('customize');
      showCustomize();
    } else if (formMode === 'customize') {
      if (addOnsEnabled) {
        setFormMode('addOns');
        showAddOns();
      } else {
        setFormMode('summary');
        showSummary();
      }
    } else if (formMode === 'addOns') {
      setFormMode('summary');
      showSummary();
    } else if (formMode === 'summary') {
      setFormMode('email');
      showQuote();
    } else {
      setFormMode('summary');
      showSummary();
    }
  };

  const emailCancel = () => {
    setFormMode('summary');
    showSummary();
  };

  const openModal = useCallback((evt) => {
    const product = { ...evt.detail };
    setModalInfo({
      title: product.title,
      price: asDollars(locale, Number(product.price)),
      productCode: product.productCode,
      productSelf: product.self,
      canAdd: product.canAdd ? true : false,
      imageSrc: getProductModalImageSrc(product.productCode)
    });
    setShowProductModal(true);
  }, []);

  const openDependsModal = useCallback((evt) => {
    const html = evt.detail.html;
    if (!html) {
      return false;
    }
    setModalInfo({ html });
    setShowDependsModal(true);
  }, []);

  const closeModal = () => {
    setShowProductModal(false);
    setShowDependsModal(false);
  };

  const addProduct = (event) => {
    event.preventDefault();
    modalInfo.productSelf ? modalInfo.productSelf.dispVal(true) : null;
    setShowProductModal(false);
  };

  const postalCodeUpdatedFromModal = (values) => {
    if (!values.postalCode) {
      window.location.reload();
    }

    setPostalCode(values.postalCode);
    setProvince(values.province);
    setLngLat(values.lngLat);
  };

  const handleSetManifestImageCode = useCallback((evt) => {
    setManifestImageCode(evt.detail?.sbdisp?.CurrentBOM.b);
  });

  const handleShouldShowPostalModal = () => {
    const cookies = Cookie.parse(document.cookie || '');
    if (!cookies.postalCode || !cookies.province) {
      setShowPostalModal(true);
    }
  };

  const buildFromKO = async () => {
    const uniqueId = getUniqueIdFromELOQUA();
    const orderSummary = window.ko.toJSON(window.build.SBDisp.SummaryObject);
    const ProductId = window.buildAppProductID;
    let url = null;
    const payload = {
      ProductId,
      SBOMId: 1, // TODO: find out what this means from Kubota
      SessionId: uniqueId,
      Details: JSON.parse(orderSummary)
    };

    try {
      const { err, result, msg } = await API.post('/api/buildandquotesave', payload);

      if (err) {
        console.error('There was an error with the buildandquote request:', result);
      }

      const route = `/buildandquote/summary/${msg}`;
      const uri = locale === 'fr' ? `/fr${route}` : `${route}`;
      url = `${window.location.origin}${uri}`;
    } catch (error) {
      console.error('There was an error with the buildandquote request:', error);
    }

    return url;
  };

  const handleShare = async () => {
    const url = await buildFromKO();

    if (url) {
      window.open(url);
      navigator.clipboard.writeText(url);
    }
  };

  async function validateCalculator(inputs) {
    const sbdisp = window.build?.SBDisp;
    let { downPayment, term, regularPayment, tradeValue } = inputs;

    const minimumDownPayment = await getValidMinimumDownPayment();
    //console.log("Validation Inputs:", { downPayment, minimumDownPayment, term, regularPayment, tradeValue });

    // Ensure downPayment is set and valid
    downPayment = downPayment === "" ? minimumDownPayment : parseFloat(downPayment);
    if (isNaN(downPayment)) {
      console.error('Invalid down payment (NaN):', downPayment);
      return false;
    }

    if (downPayment < minimumDownPayment) {
      //(`Down payment (${downPayment}) is less than minimum (${minimumDownPayment}). Setting to minimum.`);
      downPayment = minimumDownPayment;
      document.querySelector('#downPayment input').value = downPayment; // Make sure this is the correct selector
    }

    if (downPayment <= 0) {
      console.error('Invalid down payment (<=0):', downPayment);
      document.querySelector('#downPayment input').value = sbdisp.initialDownPayment();
      return false;
    }

    // Ensure tradeValue is valid
    if ((isNaN(parseFloat(tradeValue)) && tradeValue !== "") || parseFloat(tradeValue) < 0) {
      console.error('Invalid Trade-in Value', tradeValue);
      document.querySelector('#tradeAmount').value = 0;
      return false;
    }

    // Ensure term is set
    if (!term) {
      console.error('Invalid term:', term);
      document.querySelector('#payment-term > select').value = parseInt(sbdisp.initialTerm());
      return false;
    }

    // Ensure regularPayment is valid
    if ((isNaN(regularPayment) && regularPayment !== "") || parseFloat(regularPayment) < 0) {
      console.error('Invalid regular payment:', regularPayment);
      return false;
    }

    // Ensure total loan amount is valid
    if (sbdisp.ConfiguredTotal() < (downPayment + parseFloat(tradeValue))) {
      console.error('Invalid loan amount. ConfiguredTotal:', sbdisp.ConfiguredTotal(), 'Calculated Total:', downPayment + parseFloat(tradeValue));
      return false;
    }

    return true;
  }

  async function processCalculator(payment = null) {
    const options = {};
    if (payment !== null) {
      options.payment = payment; // Make sure this is just the payment value
    }

    let inputs = gatherInputValues(options);
    //console.log("Processing calculator with inputs:", inputs);

    const isValid = await validateCalculator(inputs);
    if (isValid) {
      inputs = gatherInputValues(options);
      await handleCalcPayment(inputs);
    } else {
      console.error('Validation failed. Calculation aborted.');
    }
  }

  function gatherInputValues(options = {}) {
    const downPaymentValue = document.querySelector('#downPayment input')?.value;
    const termValue = document.querySelector('#payment-term > select')?.value;
    const regularPaymentValue = isNaN(options.payment) ? 0 : options.payment; // Ensure this is the actual payment value
    const tradeInValue = document.querySelector('#tradeAmount')?.value;

    return {
      downPayment: downPaymentValue,
      term: termValue,
      regularPayment: regularPaymentValue,
      tradeValue: tradeInValue
    };
  }

  async function getValidMinimumDownPayment() {
    const sbdisp = window.build?.SBDisp;
    let minimumDownPayment = sbdisp.minimumDownPayment();

    // Ensure minimum down payment is positive
    while (minimumDownPayment <= 0) {
      console.warn('Fetching a valid minimum down payment');
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate wait for updated data
      minimumDownPayment = sbdisp.minimumDownPayment(); // Re-fetch the minimum down payment
    }
    return minimumDownPayment;
  }

  const handleCalcPayment = async (inputs) => {
    const sbdisp = window.build?.SBDisp;
    const { downPayment, term, regularPayment, tradeValue } = inputs;
    sbdisp.preferredPaymentAmount(regularPayment);
    sbdisp.preferredTerm(term);

    const payload = {
      "Productid": data?.id,
      "sbomid": 1,
      "Province": getProvince(),
      "FinanceType": sbdisp.activePHTPLN(),
      "FinancePlanType": 0,
      "PaymentFrequency": sbdisp.activePaymentFrequency(),
      "TermInMonths": term,
      "PreferredPaymentAmount": regularPayment != "" && regularPayment > 0 ? regularPayment : 0,
      "InterestRate": sbdisp.currentFinancePlan().Details.find((elem) => elem.term == term).rate,
      "FinanceFees": 0,
      "DownPayment": downPayment && downPayment != "" ? parseFloat(downPayment) : 0,
      "ResidualAmount": 0,
      "ResidualPercent": 0,
      "CashInLieu": sbdisp.CashInLieu(),
      "EquipmentTotal": sbdisp.ConfiguredTotal() - tradeValue,
    };

    try {
      const response = await API.post('/api/paymentCalc', payload);

      if (response.err) {
        console.error('There was an error with the payment calculator request:', response.msg);
      } else {
        // Set the returned values in the page
        document.querySelector('#effRate').innerHTML = " (" + (response.effRate * 100).toFixed(2) + "% APR)";
        document.querySelector('#effRate2').innerHTML = " (" + (response.effRate * 100).toFixed(2) + "% APR)";
        //document.querySelector('#downPayment input').value = response.downPayment?.toFixed(2);
        document.querySelector('#weeklyPaymentAmount').value = response.totalPayment?.toFixed(2);
        document.querySelector('#borrowingCost .amount').innerHTML = asCurrency(locale, parseFloat(response.totalCostOfBorrowing + sbdisp.CashInLieu()));
        document.querySelector('#borrowingCost').classList.remove('hidden');
        if (response.totalPayment?.toFixed(2)) {
          sbdisp.preferredPaymentAmount(response.totalPayment?.toFixed(2));
        }
      }
    } catch (error) {
      console.error('Error calling the payment calculator API:', error);
    }
  };

  const handleChangePaymentAmount = async (event) => {
    const { payment } = event.detail;
    processCalculator(payment);
  };

  const handleResetCalculator = async () => {
    await new Promise(resolve => setTimeout(async () => {
      const downPayment = await getValidMinimumDownPayment();
      document.querySelector('#downPayment input').value = downPayment;
      processCalculator();
    }, 600));
  };

  const addEventHandlers = () => {
    window.addEventListener('manifestUpdated', handleSetManifestImageCode);
    document.addEventListener('dependsModal:click', openDependsModal);
    document.addEventListener('productModal:click', openModal);
    document.addEventListener('shareButton:click', handleShare);
    document.addEventListener('copyLinkButton:click', handleShare);
    document.addEventListener('calculateButton:click', processCalculator);
    document.addEventListener('paymentAmount:change', handleChangePaymentAmount);
    document.addEventListener('resetButton:click', handleResetCalculator);
  };

  const removeEventHandlers = () => {
    window.removeEventListener('manifestUpdated', handleSetManifestImageCode);
    document.removeEventListener('productModal:click', openModal);
    document.removeEventListener('dependsModal:click', openDependsModal);
    document.removeEventListener('shareButton:click', handleShare);
    document.removeEventListener('copyLinkButton:click', handleShare);
    document.removeEventListener('calculateButton:click', processCalculator);
    document.removeEventListener('paymentAmount:change', handleChangePaymentAmount);
    document.removeEventListener('resetButton:click', handleResetCalculator);

    window.build?.SBDisp?.ConfiguredTotal?.unsubscribe()
  };

  const waitForKOBuild = async () => {
    while (!window.Build || typeof window.ko === 'undefined') {
      await new Promise(resolve => setTimeout(resolve, 200));
    }

    window.ko.cleanNode($('#bomNav'));
    // --- Give KO some time to dispose before working with it again
    await new Promise(resolve => setTimeout(() => {
      window.doKOHelper(jQuery, window.ko);
      window.build = new window.Build($('#bomNav'), {
        language: getLanguageId(locale),
      });
    }, 200));
  };

  const waitForOrderGroups = async () => {
    while (!window.build?.SBDisp.ordergroups.standard) {
      await new Promise(resolve => setTimeout(resolve, 200));
    }
    const content = window.build.SBDisp.ordergroups.content().find((el) => el.bm);
    const standard = window.build.SBDisp.ordergroups.standard[0];
    // This is where we need to insert the model designation for the image pictured.
    if (standard) {
      insertReactComponent('#manifestTitle', LegalDescription, {
        description: standard.ModelDesignation,
      });
    } else if (content) {
      insertReactComponent('#manifestTitle', LegalDescription, {
        description: content.ModelDesignation,
      });
    }

    // When manifest changes, run Calculator
    window.build?.SBDisp.ConfiguredTotal.subscribe(() => processCalculator());
  };

  /*
   * Primary useEffect Hook for any logic that needs to run on mount or unmount
   */
  useEffect(() => {
    (async () => {
      window.build = null;
      await waitForKOBuild();
    })();

    // handlers
    addEventHandlers();

    // Set up
    setPostalCode(getPostalCode());
    setProvince(getProvince());
    setLngLat(getLngLat());
    dispatch(reqLocationsAction({ latlng: `${getLngLat()}` }));

    // Conditional logic
    handleShouldShowPostalModal();

    return () => removeEventHandlers();
  }, []);

  useEffect(() => {
    (async () => {
      window.build = null;
      await waitForOrderGroups();
    })();

    if (summaryLegal?.Enabled) {
      insertReactComponent('#summaryLegalDesc', LegalDescription, {
        description: locale === 'fr' ? summaryLegal?.OptionInformation?.fr : summaryLegal?.OptionInformation?.en
      });
    }
  }, [baseModelLegal, summaryLegal]);

  /*
   * After we have the manifest image code, we can be reasonably assured the
   * Knockout templates have loaded, and we can insert our React components into the DOM.
   */
  useEffect(() => {
    if (manifestImageCode) {
      insertReactComponent('#productImages', BuildImage, {
        locale,
        imgCode: manifestImageCode,
        productDetail: data?.ProductDetail,
        darkMode: data?.DarkMode,
      });
      insertReactComponent('.continueButton', ContinueButton, {
        clicked: continueButtonClicked, locale, formMode,
      }, { isMultiple: true });
      insertReactComponent('.quoteButton', QuoteButton, {
        clicked: quoteButtonClicked, locale, formMode, addOnsEnabled,
      });
      insertReactComponent('#emailFormContainer', EmailForm, {
        cancel: emailCancel,
        dealer,
        province,
        postalCode,
        locale,
        router,
        getSummaryUrl: buildFromKO,
      });
    }
  }, [manifestImageCode, formMode]);

  useEffect(() => {
    if (document) {
      if (formMode === 'email') {
        showQuote();
      } else if (formMode === 'customize') {
        showCustomize();
      } else if (formMode === 'addOns') {
        showAddOns();
      } else if (formMode === 'summary') {
        showSummary();
      } else {
        const dispHolder = document.querySelector('#sbDispHolder');
        dispHolder?.classList.remove('summary', 'addOns', 'customize');
        window.scrollTo(0, 0);
      }
    }
  }, [formMode]);

  const handleStep = (step) => {
    switch (step) {
      case 1:
        window.location.href = `${window.origin}${(locale == 'fr' ? '/fr' : '')}/buildandquote/`;
        // router.push('/buildandquote/', undefined, { shallow: true });
        break;
      case 2:
        setFormMode('configuration');
        break;
      case 3:
        setFormMode('customize');
        showCustomize();
        break;
      case 4:
        setFormMode('addOns');
        showAddOns();
        break;
      case 5:
        setFormMode('summary');
        showSummary();
        break;
    }
  };

  const viewer360List = useMemo(() =>
    data?.ProductDetail?.ProductGallery?.find((x) => x.Description == 'B&Q')?.GalleryItems?.map((item) => {
      const content = item.ProductGalleryContent.find(
        (x) => parseInt(x.Language) === getLanguageId(locale) && x.FileNameUrl.includes("sirv") && x.FileNameUrl.includes("spin")
      );
      return content ? content.FileNameUrl : undefined;
    }).filter((x) => x)
  , [data?.ProductDetail]);

  const getFocus = () => {
    switch (formMode) {
      case 'summary':
        return 5;
      case 'email':
        return 5;
      case 'addOns':
        return 4;
      case 'customize':
        return 3;
      default:
        return 2;
    }
  };

  return (
    <PageContext.Provider value={data}>
      <Layout>
        <Container className={styles.buildCustomization}>
          <Head>
            <title>{locale == 'fr' ? 'Construire et obtenir un devis' : 'Build & Quote'} | Kubota Canada</title>
            <meta
              property="og:title"
              content={`${locale == 'fr' ? 'Construire et obtenir un devis' : 'Build & Quote'} | Kubota Canada`}
            />

            <script> window.apiBaseURL = `{NEXT_PUBLIC_API_BASE_URL}`; </script>
            <script> window.buildAppProductID = {id}; </script>

            <script src="/scripts/jquery-3.5.1.min.js" />
            <script src="/scripts/vendor/knockout/knockout-3.5.0.js" type="text/javascript" />
            <script src="/scripts/vendor/knockout/koHelper.js?build=" type="text/javascript" />
            <script src="/scripts/bom/SBDispItem.js?build=" type="text/javascript" />
            <script src="/scripts/bom/OrderGroup.js?build=" type="text/javascript" />
            <script src="/scripts/bom/manifest.js?build=" type="text/javascript" />
            <script src="/scripts/bom/finder.js?build=" type="text/javascript" />
            <script src="/scripts/bom/CommonDisp.js?build=" type="text/javascript" />
            <script src="/scripts/bom/InitBuild.js?build=" type="text/javascript" />
            <script src="/scripts/bom/BOMNote.js?build=" type="text/javascript" />
            <script src="/scripts/buildApp.js?build=" type="text/javascript" />
            <script src="/scripts/models/build.js?build=" type="text/javascript" />
            <script src="/scripts/numUtils.js?build=" type="text/javascript" />

            {viewer360List?.length > 0 && (
              <>
                <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/pannellum@2.5.4/build/pannellum.css" />
                <link rel="stylesheet" href="/styles/hotspots.css" />
                <script src="https://cdn.jsdelivr.net/npm/pannellum@2.5.4/build/pannellum.js"></script>
                <script src="https://scripts.sirv.com/sirvjs/v3/sirv.js"></script>
                <script src="/scripts/hotspots.js"></script>
              </>
            )}
          </Head>

          <DealerHeader />

          <StepProgress
            steps={5}
            focus={getFocus()}
            texts={
              locale == 'fr' ?
                ['Sélectionner un produit', 'Choix du modèle', 'Compléments Kubota', 'Compléments Land Pride', 'Sommaire'] :
                ['Select a Product', 'Model Choice', 'Kubota Add-Ons', 'Land Pride Add-Ons', 'Summary']
            }
            hidden={addOnsEnabled ? null : 4}
            handleStep={handleStep}
          />

          <div dangerouslySetInnerHTML={{ __html: (locale == 'fr' ? FrPostTextBody : EnPostTextBody) }}></div>
          <div dangerouslySetInnerHTML={{ __html: (locale == 'fr' ? FrSectionBody0 : EnSectionBody0) }}></div>
          <div dangerouslySetInnerHTML={{ __html: (locale == 'fr' ? FrSectionBody1 : EnSectionBody1) }}></div>
          <div dangerouslySetInnerHTML={{ __html: (locale == 'fr' ? FrSectionBody2 : EnSectionBody2) }}></div>

          <div id="bomNav" dangerouslySetInnerHTML={{ __html: '<!-- ko template: "koBuildMain" --> <!-- /ko -->' }}></div>

          <div id="productModal"></div>

          <Modal className={styles.customizationModal} show={showProductModal} onHide={() => setShowProductModal(false)}>
            <div className={styles.productImg}>
              <img
                src={modalInfo.imageSrc}
                onError={({ currentTarget }) => {
                  currentTarget.onerror = null;
                  currentTarget.src = '/api/static/media/byfile/Default/Backup/1_N_L_N_I_640X360.JPG';
                }}
              />
            </div>
            <div className={styles.title}>{modalInfo.title}</div>
            <div className={styles.price}>{modalInfo.price}*</div>
            <div className={styles.legal}>
              {locale == 'fr' ? '*Includes installation costs where applicable': '*Includes installation costs where applicable'}
            </div>

            <div className={styles.buttons}>
              {modalInfo.canAdd && <Button onClick={addProduct}>Add Option</Button>}
              <Button onClick={closeModal} className={styles.btn_cancel}>
                {locale == 'fr' ? 'Fermer la fenêtre' : 'Close Window'}
              </Button>
            </div>
          </Modal>

          <Modal
            className={styles.customizationModal + ' ' + styles.dependsModal}
            show={showDependsModal}
            onHide={() => setShowDependsModal(false)}
          >
            <div className={styles.dependsModal} dangerouslySetInnerHTML={{ __html: modalInfo.html }}></div>
            <div className={styles.buttons}>
              <Button onClick={closeModal} className={styles.btn_cancel}>
                {locale == 'fr' ? 'Fermer la fenêtre' : 'Close Window'}
              </Button>
            </div>
          </Modal>

          <PostalCodeModal
            showPostalModal={showPostalModal}
            setShowPostalModal={setShowPostalModal}
            updatePostalCode={postalCodeUpdatedFromModal}
          />
        </Container>
      </Layout>
    </PageContext.Provider>
  );
}

export const getServerSideProps = async ({ params, locale }) => {
  const { id } = params;
  const { data } = await apiClient.get('/api/Posts/1000972');

  // TODO: is this needed?
  // const postData = formatPostData(data);

  /* prettier-ignore */
  const checkTheme = data?.DisplayOptions
    ?.find((x) => x.Name == 'Header')
    ?.DisplayOptions
    ?.find((item) => item.DisplayOptionKey == 'DarkMode')
    ?.Enabled;


  data.DarkMode = checkTheme ? checkTheme : null;

  const translations = await serverSideTranslations(locale, ['common', 'footer']);

  const {
    data: { product }
  } = await apiClient.get(`/api/Products/${id}/-1`, { params: { ActiveOnly: true } });
  if (product) {
    data['ProductDetail'] = product;
  }

  return {
    props: {
      data,
      ...translations
    }
  };
};
